import { collection, addDoc } from 'firebase/firestore';
import type { SecurityEventRecord, SecurityEventType } from '../types';
import { getDb } from './firebase';

const SECURITY_EVENTS_COLLECTION = 'security_events';

const XSS_PATTERNS: RegExp[] = [
  /<script\b/i,
  /onerror\s*=/i,
  /onload\s*=/i,
  /javascript:/i,
  /<\/?iframe\b/i,
];

const SQLI_PATTERNS: RegExp[] = [
  /(['"]\s*or\s+1=1)/i,
  /\bunion\s+select\b/i,
  /\bdrop\s+table\b/i,
  /\binsert\s+into\b/i,
];

const PROMPT_INJECTION_PATTERNS: RegExp[] = [
  /ignore\s+previous\s+instructions/i,
  /act\s+as\s+an?\s+ai/i,
  /you\s+are\s+chatgpt/i,
  /system:\s*/i,
];

export interface TextAnalysisResult {
  isSuspicious: boolean;
  type?: SecurityEventType;
  severity?: 'low' | 'medium' | 'high';
  reasons: string[];
}

/** Analiza un texto libre buscando patrones típicos de ataques o prompt injection. */
export function analyzeTextForAttacks(text: string): TextAnalysisResult {
  const reasons: string[] = [];
  let type: SecurityEventType | undefined;
  let severity: 'low' | 'medium' | 'high' | undefined;

  if (XSS_PATTERNS.some((re) => re.test(text))) {
    reasons.push('Patrón similar a XSS (etiquetas o handlers JS)');
    type = 'suspected_xss';
    severity = 'high';
  }

  if (SQLI_PATTERNS.some((re) => re.test(text))) {
    reasons.push('Patrón similar a SQL injection');
    type = type ?? 'suspected_sqli';
    severity = severity ?? 'high';
  }

  if (PROMPT_INJECTION_PATTERNS.some((re) => re.test(text))) {
    reasons.push('Patrón típico de prompt injection hacia modelos de IA');
    type = type ?? 'prompt_injection';
    severity = severity ?? 'medium';
  }

  if (text.length > 3000) {
    reasons.push('Texto muy largo (posible payload malicioso o abuso)');
    type = type ?? 'suspicious_pattern';
    severity = severity ?? 'medium';
  }

  return {
    isSuspicious: reasons.length > 0,
    type,
    severity,
    reasons,
  };
}

/** Registro de un evento de seguridad en Firestore. */
export async function logSecurityEvent(
  siteId: string,
  payload: Omit<SecurityEventRecord, 'siteId' | 'recordedAt' | 'userAgent' | 'url'> & {
    sample?: string;
  },
): Promise<void> {
  const db = getDb();
  if (!db) return;

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const url = typeof window !== 'undefined' ? window.location.href : '';

  const record: SecurityEventRecord = {
    siteId,
    eventType: payload.eventType,
    severity: payload.severity,
    source: payload.source,
    userAgent: ua,
    url,
    sample: payload.sample?.slice(0, 500),
    metadata: payload.metadata,
    recordedAt: Date.now(),
  };

  try {
    await addDoc(collection(db, 'sites', siteId, SECURITY_EVENTS_COLLECTION), record);
  } catch (e) {
    if (typeof console !== 'undefined') {
      console.warn('[Kenbun] Error logging security event:', e);
    }
  }
}

/** Rate‑limit muy sencillo por sesión para evitar explosiones de eventos de seguridad. */
const RATE_LIMIT_KEY = 'kenbun_sec_rl';
const RATE_LIMIT_MAX_EVENTS = 50;
const RATE_LIMIT_WINDOW_MS = 30 * 60 * 1000; // 30 minutos

export function isRateLimitedForSecurity(): boolean {
  if (typeof sessionStorage === 'undefined') return false;

  const now = Date.now();
  const raw = sessionStorage.getItem(RATE_LIMIT_KEY);
  let data: { count: number; ts: number } = raw ? JSON.parse(raw) : { count: 0, ts: now };

  if (now - data.ts > RATE_LIMIT_WINDOW_MS) {
    data = { count: 0, ts: now };
  }

  data.count += 1;
  sessionStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));

  return data.count > RATE_LIMIT_MAX_EVENTS;
}

